import { selectQuotationInsulatedBoxStructure } from "app/containers/WorkOrder/redux/selector";
import { actions } from "app/containers/WorkOrder/redux/slice";
import { QuotationInsulatedBoxCardKey } from "app/containers/WorkOrder/types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  INSULATED_HYDRAULIC_TON_OPTIONS,
  INSULATED_REAR_FRAME_OPTIONS,
  INSULATED_REAR_OPENING_OPTIONS,
  INSULATED_ROOF_TYPE_OPTIONS,
  INSULATED_SIDE_FLAP_OPTIONS,
} from "../../../data";
import { formKey } from "../../../hooks/useQuoteBoxStructure";
import { QuoteSelectOption } from "../../../types";
import {
  FieldsRow,
  StructureCard,
} from "../OpenBoxQuotes/BoxStructureFields";
import { NoteField, NumberField, SelectField } from "../OpenBoxQuotes/FormFields";

export const insulatedKey = (suffix: string) =>
  formKey(`INSULATED_BOX.${suffix}`);

export const ROOF_TYPE = INSULATED_ROOF_TYPE_OPTIONS;
export const REAR_FRAME = INSULATED_REAR_FRAME_OPTIONS;
export const REAR_OPENING = INSULATED_REAR_OPENING_OPTIONS;
export const HYDRAULIC_TON = INSULATED_HYDRAULIC_TON_OPTIONS;
export const SIDE_FLAP = INSULATED_SIDE_FLAP_OPTIONS;

export const useInsulatedBoxStructure = () =>
  useSelector(selectQuotationInsulatedBoxStructure);

export const TypeCard = ({
  cardKey,
  titleKey,
  options,
  selectLabelKey = insulatedKey("FIELDS.TYPE"),
}: {
  cardKey: QuotationInsulatedBoxCardKey;
  titleKey: string;
  options: QuoteSelectOption[];
  selectLabelKey?: string;
}) => {
  const dispatch = useDispatch();
  const item = useInsulatedBoxStructure()[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) =>
        dispatch(actions.updateQuotationInsulatedBoxStructure({ key: cardKey, value: { enabled } }))
      }
    >
      <SelectField
        labelKey={selectLabelKey}
        value={item.selectedOption}
        options={options}
        onChange={(selectedOption) =>
          dispatch(actions.updateQuotationInsulatedBoxStructure({ key: cardKey, value: { selectedOption } }))
        }
      />
    </StructureCard>
  );
};

export const NoteCard = ({
  cardKey,
  titleKey,
}: {
  cardKey: QuotationInsulatedBoxCardKey;
  titleKey: string;
}) => {
  const dispatch = useDispatch();
  const item = useInsulatedBoxStructure()[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) =>
        dispatch(actions.updateQuotationInsulatedBoxStructure({ key: cardKey, value: { enabled } }))
      }
    >
      <NoteField
        labelKey={insulatedKey("FIELDS.NOTE")}
        placeholderKey={insulatedKey("FIELDS.NOTE")}
        value={item.note}
        onChange={(note) =>
          dispatch(actions.updateQuotationInsulatedBoxStructure({ key: cardKey, value: { note } }))
        }
      />
    </StructureCard>
  );
};

export const ThicknessCard = ({
  cardKey,
  titleKey,
}: {
  cardKey: QuotationInsulatedBoxCardKey;
  titleKey: string;
}) => {
  const dispatch = useDispatch();
  const item = useInsulatedBoxStructure()[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) =>
        dispatch(actions.updateQuotationInsulatedBoxStructure({ key: cardKey, value: { enabled } }))
      }
    >
      <NumberField
        labelKey={insulatedKey("FIELDS.THICKNESS_MM")}
        value={item.size}
        onChange={(size) =>
          dispatch(actions.updateQuotationInsulatedBoxStructure({ key: cardKey, value: { size } }))
        }
      />
    </StructureCard>
  );
};

export const SideDoorOpeningsCard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const item = useInsulatedBoxStructure().sideDoorOpenings;
  const note = item.note.startsWith("QUOTES.") ? t(item.note) : item.note;

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={insulatedKey("CARDS.SIDE_DOOR_OPENINGS")}
      onToggle={(enabled) =>
        dispatch(actions.updateQuotationInsulatedBoxStructure({ key: "sideDoorOpenings", value: { enabled } }))
      }
    >
      <FieldsRow>
        <NumberField
          labelKey={insulatedKey("FIELDS.QUANTITY")}
          value={item.quantity}
          onChange={(quantity) =>
            dispatch(actions.updateQuotationInsulatedBoxStructure({ key: "sideDoorOpenings", value: { quantity } }))
          }
          grow
        />
        <NumberField
          labelKey={insulatedKey("FIELDS.LENGTH_MM")}
          value={item.length}
          onChange={(length) =>
            dispatch(actions.updateQuotationInsulatedBoxStructure({ key: "sideDoorOpenings", value: { length } }))
          }
          grow
        />
        <NumberField
          labelKey={insulatedKey("FIELDS.DOOR_THICKNESS_MM")}
          value={item.size}
          onChange={(size) =>
            dispatch(actions.updateQuotationInsulatedBoxStructure({ key: "sideDoorOpenings", value: { size } }))
          }
          grow
        />
        <NoteField
          labelKey={insulatedKey("FIELDS.NOTE_LOCATION")}
          placeholderKey={insulatedKey("FIELDS.NOTE_LOCATION")}
          value={note}
          onChange={(nextNote) =>
            dispatch(actions.updateQuotationInsulatedBoxStructure({ key: "sideDoorOpenings", value: { note: nextNote } }))
          }
          grow
        />
      </FieldsRow>
    </StructureCard>
  );
};
